import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Upload, Loader2, AlertCircle, Search, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ModelsTab from './Modelstab';
import VariantsTab from './Variantstab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// EV subcategory options — a brand tagged with 'ev' can serve one or more of these
const EV_SUB_OPTIONS = [
  { value: 'car',        label: 'EV Car',        icon: '🚗' },
  { value: 'bike',       label: 'EV Bike',       icon: '🏍️' },
  { value: 'commercial', label: 'EV Commercial', icon: '🚛' },
];

const SuperAdminManagement = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [features, setFeatures] = useState([]);
  const [rtoCodes, setRTOCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [brandSearch, setBrandSearch] = useState('');
  const [brandCategoryFilter, setBrandCategoryFilter] = useState('all');

  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentTab, setCurrentTab] = useState(searchParams.get('tab') || 'brands');

  // Brand form — ev_subtypes is a string[] (multi-select checkboxes)
  const [brandForm, setBrandForm] = useState({ name: '', logo: '', category: '', ev_subtypes: [] });
  const [featureForm, setFeatureForm] = useState({ name: '', category: '', applicable_to: [] });
  const [rtoForm, setRTOForm] = useState({ code: '', state: '' });
  const [cityForm, setCityForm] = useState({ name: '', qr_code: '', validity_days: 30, upload_price: 500 });

  useEffect(() => { fetchAllData(); }, []);
  useEffect(() => { const tab = searchParams.get('tab'); if (tab) setCurrentTab(tab); }, [searchParams]);

  const fetchAllData = async () => {
    try {
      const [citiesRes, brandsRes, featuresRes, rtoRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/brands`),
        axios.get(`${API}/features`),
        axios.get(`${API}/rto-codes`)
      ]);
      setCities(citiesRes.data.cities);
      setBrands(brandsRes.data.brands);
      setFeatures(featuresRes.data.features);
      setRTOCodes(rtoRes.data.rto_codes);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  // Filtered brands
  const filteredBrands = brands.filter(b => {
    const matchSearch = !brandSearch.trim() || b.name?.toLowerCase().includes(brandSearch.trim().toLowerCase());
    const matchCategory = brandCategoryFilter === 'all' || b.category === brandCategoryFilter;
    return matchSearch && matchCategory;
  });

  const handleImageUpload = (e, setForm) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setForm(prev => ({ ...prev, logo: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCityForm(prev => ({ ...prev, qr_code: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  // Toggle an EV sub-category in the brand form
  const toggleEvSubtype = (val) => {
    setBrandForm(prev => ({
      ...prev,
      ev_subtypes: prev.ev_subtypes.includes(val)
        ? prev.ev_subtypes.filter(s => s !== val)
        : [...prev.ev_subtypes, val]
    }));
  };

  // ── BRAND CRUD ──────────────────────────────────────────────────────────
  const handleCreateBrand = async () => {
    if (!brandForm.name || !brandForm.category) { toast.error('Please fill all required fields'); return; }
    if (brandForm.category === 'ev' && brandForm.ev_subtypes.length === 0) {
      toast.error('Please select at least one EV sub-category (Car, Bike, or Commercial)');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: brandForm.name,
        logo: brandForm.logo,
        category: brandForm.category,
        // Only send ev_subtypes for EV brands
        ev_subtypes: brandForm.category === 'ev' ? brandForm.ev_subtypes : [],
      };
      if (editingItem) {
        await axios.put(`${API}/superadmin/brands/${editingItem}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Brand updated successfully');
      } else {
        await axios.post(`${API}/superadmin/brands`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Brand created successfully');
      }
      fetchAllData(); setShowDialog(false); resetBrandForm();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save brand'); }
    finally { setLoading(false); }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Delete this brand? This will also delete all associated models and variants.')) return;
    try {
      await axios.delete(`${API}/superadmin/brands/${brandId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Brand deleted successfully'); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete brand'); }
  };

  const editBrand = (brand) => {
    setBrandForm({
      name: brand.name,
      logo: brand.logo || '',
      category: brand.category,
      // Populate from ev_subtypes array, or fall back to legacy sub_category string
      ev_subtypes: brand.ev_subtypes?.length
        ? brand.ev_subtypes
        : brand.sub_category
        ? [brand.sub_category]
        : [],
    });
    setEditingItem(brand.id); setShowDialog(true);
  };

  const resetBrandForm = () => { setBrandForm({ name: '', logo: '', category: '', ev_subtypes: [] }); setEditingItem(null); };

  // ── FEATURE CRUD ────────────────────────────────────────────────────────
  const handleCreateFeature = async () => {
    if (!featureForm.name || !featureForm.category || featureForm.applicable_to.length === 0) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/superadmin/features/${editingItem}`, featureForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Feature updated successfully');
      } else {
        await axios.post(`${API}/superadmin/features`, featureForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Feature created successfully');
      }
      fetchAllData(); setShowDialog(false); resetFeatureForm();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save feature'); }
    finally { setLoading(false); }
  };

  const handleDeleteFeature = async (featureId) => {
    if (!window.confirm('Delete this feature?')) return;
    try { await axios.delete(`${API}/superadmin/features/${featureId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Feature deleted successfully'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete feature'); }
  };

  const editFeature = (feature) => { setFeatureForm({ name: feature.name, category: feature.category, applicable_to: feature.applicable_to || [] }); setEditingItem(feature.id); setShowDialog(true); };
  const resetFeatureForm = () => { setFeatureForm({ name: '', category: '', applicable_to: [] }); setEditingItem(null); };

  // ── RTO CRUD ────────────────────────────────────────────────────────────
  const handleCreateRTO = async () => {
    if (!rtoForm.code || !rtoForm.state) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/superadmin/rto-codes/${editingItem}`, rtoForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('RTO code updated successfully');
      } else {
        await axios.post(`${API}/superadmin/rto-codes`, rtoForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('RTO code created successfully');
      }
      fetchAllData(); setShowDialog(false); resetRTOForm();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save RTO code'); }
    finally { setLoading(false); }
  };

  const handleDeleteRTO = async (rtoId) => {
    if (!window.confirm('Delete this RTO code?')) return;
    try { await axios.delete(`${API}/superadmin/rto-codes/${rtoId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('RTO code deleted'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete RTO code'); }
  };

  const editRTO = (rto) => { setRTOForm({ code: rto.code, state: rto.state }); setEditingItem(rto.id); setShowDialog(true); };
  const resetRTOForm = () => { setRTOForm({ code: '', state: '' }); setEditingItem(null); };

  // ── CITY CRUD ───────────────────────────────────────────────────────────
  const handleCreateCity = async () => {
    if (!cityForm.name) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/superadmin/cities/${editingItem}`, cityForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('City updated successfully');
      } else {
        await axios.post(`${API}/superadmin/cities`, cityForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('City created successfully');
      }
      fetchAllData(); setShowDialog(false); resetCityForm();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save city'); }
    finally { setLoading(false); }
  };

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('Delete this city?')) return;
    try { await axios.delete(`${API}/superadmin/cities/${cityId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('City deleted'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete city'); }
  };

  const editCity = (city) => { setCityForm({ name: city.name, qr_code: city.qr_code || '', validity_days: city.validity_days || 30, upload_price: city.upload_price || 500 }); setEditingItem(city.id); setShowDialog(true); };
  const resetCityForm = () => { setCityForm({ name: '', qr_code: '', validity_days: 30, upload_price: 500 }); setEditingItem(null); };

  const handleDialogClose = (open) => {
    if (!open) { setEditingItem(null); resetBrandForm(); resetFeatureForm(); resetRTOForm(); resetCityForm(); }
    setShowDialog(open);
  };

  const categoryColors = {
    car: 'bg-blue-100 text-blue-700', bike: 'bg-orange-100 text-orange-700',
    ev: 'bg-green-100 text-green-700', commercial: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-800 mb-2">Master Data Management</h1>
          <p className="text-secondary-600">Manage all platform master data — brands, models, variants, features, RTO codes, and cities</p>
        </div>

        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          <TabsList className="grid grid-cols-6 w-full mb-6">
            <TabsTrigger value="brands"   data-testid="brands-tab">Brands</TabsTrigger>
            <TabsTrigger value="models"   data-testid="models-tab">Models</TabsTrigger>
            <TabsTrigger value="variants" data-testid="variants-tab">Variants</TabsTrigger>
            <TabsTrigger value="features" data-testid="features-tab">Features</TabsTrigger>
            <TabsTrigger value="rto"      data-testid="rto-tab">RTO Codes</TabsTrigger>
            <TabsTrigger value="cities"   data-testid="cities-tab">Cities</TabsTrigger>
          </TabsList>

          {/* ════════ BRANDS TAB ════════ */}
          <TabsContent value="brands">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle>
                        Brand Management
                        <span className="text-sm font-normal text-gray-400 ml-2">
                          ({filteredBrands.length} of {brands.length})
                        </span>
                      </CardTitle>
                    </div>

                    <Dialog open={showDialog && currentTab === 'brands'} onOpenChange={handleDialogClose}>
                      <DialogTrigger asChild>
                        <Button className="bg-primary-500 text-black hover:bg-primary-600" data-testid="add-brand-btn" onClick={resetBrandForm}>
                          <Plus className="mr-2 h-4 w-4" /> Add Brand
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>{editingItem ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                          <div>
                            <Label>Brand Name *</Label>
                            <Input value={brandForm.name} onChange={e => setBrandForm({ ...brandForm, name: e.target.value })} placeholder="e.g., Tata Motors" data-testid="brand-name-input" />
                          </div>

                          {/* Category */}
                          <div>
                            <Label>Category *</Label>
                            <Select value={brandForm.category} onValueChange={val => setBrandForm({ ...brandForm, category: val, ev_subtypes: [] })}>
                              <SelectTrigger data-testid="brand-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="car">Car</SelectItem>
                                <SelectItem value="bike">Bike</SelectItem>
                                <SelectItem value="ev">EV (Electric Vehicle)</SelectItem>
                                <SelectItem value="commercial">Commercial</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* EV Sub-categories — shown only when category is EV */}
                          {brandForm.category === 'ev' && (
                            <div>
                              <Label className="mb-2 block">
                                EV Sub-categories *
                                <span className="ml-2 text-xs text-gray-400 font-normal">Select all that apply</span>
                              </Label>
                              <div className="grid grid-cols-3 gap-2">
                                {EV_SUB_OPTIONS.map(opt => {
                                  const isChecked = brandForm.ev_subtypes.includes(opt.value);
                                  return (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => toggleEvSubtype(opt.value)}
                                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${
                                        isChecked
                                          ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-green-300 hover:bg-green-50/50'
                                      }`}
                                    >
                                      <span className="text-xl leading-none">{opt.icon}</span>
                                      <span className="text-xs font-semibold leading-tight">{opt.label}</span>
                                      {isChecked && (
                                        <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded font-bold">✓</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              {brandForm.ev_subtypes.length === 0 && (
                                <p className="text-xs text-amber-600 mt-1.5">⚠️ Select at least one EV sub-category.</p>
                              )}
                              {brandForm.ev_subtypes.length > 0 && (
                                <p className="text-xs text-green-600 mt-1.5">
                                  ✓ This brand will appear for: <strong>{brandForm.ev_subtypes.map(s => EV_SUB_OPTIONS.find(o => o.value === s)?.label).join(', ')}</strong>
                                </p>
                              )}
                            </div>
                          )}

                          {/* Logo */}
                          <div>
                            <Label>Brand Logo</Label>
                            <Input type="file" accept="image/*" onChange={e => handleImageUpload(e, setBrandForm)} data-testid="brand-logo-input" />
                            {brandForm.logo && (
                              <div className="mt-2 relative inline-block">
                                <img src={brandForm.logo} alt="Preview" className="w-20 h-20 object-contain border rounded" />
                                <Button type="button" variant="destructive" size="sm" className="absolute -top-2 -right-2 h-6 w-6 p-0" onClick={() => setBrandForm({ ...brandForm, logo: '' })}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>

                          <Button onClick={handleCreateBrand} disabled={loading} className="w-full bg-green-500 hover:bg-green-600" data-testid="save-brand-btn">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {editingItem ? 'Update Brand' : 'Create Brand'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Search + filter */}
                  <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <Input value={brandSearch} onChange={e => setBrandSearch(e.target.value)} placeholder="Search by brand name..." className="pl-9 border-gray-200 focus:border-blue-400" />
                      {brandSearch && <button onClick={() => setBrandSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium">✕ Clear</button>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['all', 'car', 'bike', 'ev', 'commercial'].map(cat => (
                        <button key={cat} onClick={() => setBrandCategoryFilter(cat)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${brandCategoryFilter === cat ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'}`}>
                          {cat === 'all' ? 'All Categories' : cat}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {filteredBrands.length === 0 ? (
                  <div className="text-center py-12 text-secondary-500">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">
                      {brandSearch || brandCategoryFilter !== 'all'
                        ? `No brands found${brandSearch ? ` matching "${brandSearch}"` : ''}${brandCategoryFilter !== 'all' ? ` in "${brandCategoryFilter}"` : ''}.`
                        : 'No brands found. Add your first brand!'}
                    </p>
                    {(brandSearch || brandCategoryFilter !== 'all') && (
                      <button onClick={() => { setBrandSearch(''); setBrandCategoryFilter('all'); }} className="mt-2 text-sm text-blue-500 hover:underline">Clear filters</button>
                    )}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBrands.map((brand, idx) => (
                      <Card key={brand.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`brand-card-${idx}`}>
                        <div className="flex items-center gap-3">
                          {brand.logo ? (
                            <img src={brand.logo} alt={brand.name} className="w-12 h-12 object-contain rounded border bg-white p-0.5" />
                          ) : (
                            <div className="w-12 h-12 bg-secondary-100 rounded flex items-center justify-center text-secondary-400 font-bold text-lg">{brand.name?.charAt(0)}</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-secondary-800 truncate">{brand.name}</h3>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              <span className={`inline-block text-xs px-2 py-0.5 rounded-full capitalize font-medium ${categoryColors[brand.category] || 'bg-gray-100 text-gray-600'}`}>
                                {brand.category}
                              </span>
                              {/* Show EV sub-category badges */}
                              {brand.category === 'ev' && (brand.ev_subtypes?.length > 0) && (
                                brand.ev_subtypes.map(sub => (
                                  <span key={sub} className="inline-block text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium capitalize">
                                    {EV_SUB_OPTIONS.find(o => o.value === sub)?.icon} {sub}
                                  </span>
                                ))
                              )}
                              {/* Legacy fallback */}
                              {brand.category === 'ev' && !brand.ev_subtypes?.length && brand.sub_category && (
                                <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium capitalize">
                                  {brand.sub_category}
                                </span>
                              )}
                              {/* Warning if EV brand has no sub-category */}
                              {brand.category === 'ev' && !brand.ev_subtypes?.length && !brand.sub_category && (
                                <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">⚠️ No sub-cat</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="outline" size="sm" onClick={() => { editBrand(brand); setShowDialog(true); }} data-testid={`edit-brand-btn-${idx}`}><Edit className="h-4 w-4" /></Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteBrand(brand.id)} data-testid={`delete-brand-btn-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ════════ MODELS TAB ════════ */}
          <TabsContent value="models">
            <ModelsTab brands={brands} onDataChange={fetchAllData} />
          </TabsContent>

          {/* ════════ VARIANTS TAB ════════ */}
          <TabsContent value="variants">
            <VariantsTab brands={brands} onDataChange={fetchAllData} />
          </TabsContent>

          {/* ════════ FEATURES TAB ════════ */}
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Feature Management ({features.length})</CardTitle>
                  <Dialog open={showDialog && currentTab === 'features'} onOpenChange={handleDialogClose}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary-500 text-black hover:bg-primary-600" data-testid="add-feature-btn" onClick={resetFeatureForm}><Plus className="mr-2 h-4 w-4" /> Add Feature</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{editingItem ? 'Edit Feature' : 'Add New Feature'}</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div><Label>Feature Name *</Label><Input value={featureForm.name} onChange={e => setFeatureForm({ ...featureForm, name: e.target.value })} placeholder="e.g., Sunroof" data-testid="feature-name-input" /></div>
                        <div>
                          <Label>Category *</Label>
                          <Select value={featureForm.category} onValueChange={val => setFeatureForm({ ...featureForm, category: val })}>
                            <SelectTrigger data-testid="feature-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                            <SelectContent>
                              {['Safety', 'Comfort', 'Technology', 'Exterior', 'Interior', 'Performance'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Applicable To *</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {['car', 'bike', 'ev', 'commercial'].map(type => (
                              <Button key={type} type="button"
                                variant={featureForm.applicable_to.includes(type) ? 'default' : 'outline'} size="sm"
                                onClick={() => { const n = featureForm.applicable_to.includes(type) ? featureForm.applicable_to.filter(t => t !== type) : [...featureForm.applicable_to, type]; setFeatureForm({ ...featureForm, applicable_to: n }); }}
                                data-testid={`feature-applicable-${type}`}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <Button onClick={handleCreateFeature} disabled={loading} className="w-full bg-green-500 hover:bg-green-600" data-testid="save-feature-btn">
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingItem ? 'Update Feature' : 'Create Feature'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {features.length === 0 ? (
                  <div className="text-center py-8 text-secondary-500"><AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No features found. Add your first feature!</p></div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {features.map((feature, idx) => (
                      <div key={feature.id} className="p-4 border rounded-lg hover:bg-secondary-50" data-testid={`feature-card-${idx}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-secondary-800">{feature.name}</h3>
                            <p className="text-sm text-secondary-600">{feature.category}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {feature.applicable_to?.map(type => <span key={type} className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full capitalize">{type}</span>)}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => { editFeature(feature); setShowDialog(true); }} data-testid={`edit-feature-btn-${idx}`}><Edit className="h-4 w-4" /></Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteFeature(feature.id)} data-testid={`delete-feature-btn-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ════════ RTO CODES TAB ════════ */}
          <TabsContent value="rto">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>RTO Code Management ({rtoCodes.length})</CardTitle>
                  <Dialog open={showDialog && currentTab === 'rto'} onOpenChange={handleDialogClose}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary-500 text-black hover:bg-primary-600" data-testid="add-rto-btn" onClick={resetRTOForm}><Plus className="mr-2 h-4 w-4" /> Add RTO Code</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{editingItem ? 'Edit RTO Code' : 'Add New RTO Code'}</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div><Label>RTO Code * (2 letters)</Label><Input value={rtoForm.code} onChange={e => setRTOForm({ ...rtoForm, code: e.target.value.toUpperCase() })} placeholder="e.g., KA" maxLength={2} data-testid="rto-code-input" /></div>
                        <div><Label>State *</Label><Input value={rtoForm.state} onChange={e => setRTOForm({ ...rtoForm, state: e.target.value })} placeholder="e.g., Karnataka" data-testid="rto-state-input" /></div>
                        <Button onClick={handleCreateRTO} disabled={loading} className="w-full bg-green-500 hover:bg-green-600" data-testid="save-rto-btn">
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingItem ? 'Update RTO Code' : 'Create RTO Code'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {rtoCodes.length === 0 ? (
                  <div className="text-center py-8 text-secondary-500"><AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No RTO codes found. Add your first RTO code!</p></div>
                ) : (
                  <div className="grid md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {rtoCodes.map((rto, idx) => (
                      <div key={rto.id} className="p-4 border rounded-lg text-center hover:bg-secondary-50" data-testid={`rto-card-${idx}`}>
                        <h3 className="text-2xl font-bold text-primary-500">{rto.code}</h3>
                        <p className="text-sm text-secondary-600 mb-3">{rto.state}</p>
                        <div className="flex gap-1 justify-center">
                          <Button variant="outline" size="sm" onClick={() => { editRTO(rto); setShowDialog(true); }} data-testid={`edit-rto-btn-${idx}`}><Edit className="h-3 w-3" /></Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteRTO(rto.id)} data-testid={`delete-rto-btn-${idx}`}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ════════ CITIES TAB ════════ */}
          <TabsContent value="cities">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>City Management ({cities.length})</CardTitle>
                  <Dialog open={showDialog && currentTab === 'cities'} onOpenChange={handleDialogClose}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary-500 text-black hover:bg-primary-600" data-testid="add-city-btn" onClick={resetCityForm}><Plus className="mr-2 h-4 w-4" /> Add City</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{editingItem ? 'Edit City' : 'Add New City'}</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div><Label>City Name *</Label><Input value={cityForm.name} onChange={e => setCityForm({ ...cityForm, name: e.target.value })} placeholder="e.g., Belgaum" data-testid="city-name-input" /></div>
                        <div><Label>Upload Price (Rs) *</Label><Input type="number" value={cityForm.upload_price} onChange={e => setCityForm({ ...cityForm, upload_price: parseFloat(e.target.value) })} data-testid="city-price-input" /></div>
                        <div><Label>Validity Days *</Label><Input type="number" value={cityForm.validity_days} onChange={e => setCityForm({ ...cityForm, validity_days: parseInt(e.target.value) })} data-testid="city-validity-input" /></div>
                        <div>
                          <Label>Payment QR Code</Label>
                          <Input type="file" accept="image/*" onChange={handleQRUpload} data-testid="city-qr-input" />
                          {cityForm.qr_code && (
                            <div className="mt-2 relative inline-block">
                              <img src={cityForm.qr_code} alt="QR Preview" className="w-32 h-32 object-contain border rounded" />
                              <Button type="button" variant="destructive" size="sm" className="absolute -top-2 -right-2 h-6 w-6 p-0" onClick={() => setCityForm({ ...cityForm, qr_code: '' })}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          )}
                        </div>
                        <Button onClick={handleCreateCity} disabled={loading} className="w-full bg-green-500 hover:bg-green-600" data-testid="save-city-btn">
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingItem ? 'Update City' : 'Create City'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {cities.length === 0 ? (
                  <div className="text-center py-8 text-secondary-500"><AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No cities found. Add your first city!</p></div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cities.map((city, idx) => (
                      <Card key={city.id} className="p-4" data-testid={`city-card-${idx}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-xl font-semibold text-secondary-800 mb-2">{city.name}</h3>
                            <div className="space-y-1 text-sm text-secondary-600">
                              <p>Upload Price: <span className="font-semibold">Rs {city.upload_price}/-</span></p>
                              <p>Validity: <span className="font-semibold">{city.validity_days} days</span></p>
                            </div>
                            {city.qr_code && <img src={city.qr_code} alt="QR Code" className="w-16 h-16 object-contain border rounded mt-2" />}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => { editCity(city); setShowDialog(true); }} data-testid={`edit-city-btn-${idx}`}><Edit className="h-4 w-4" /></Button>
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteCity(city.id)} data-testid={`delete-city-btn-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default SuperAdminManagement;